import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Award, Loader2, Calculator, BadgeCheck, Check, X, Trophy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Excelencia() {
  const { scopeParams, selectedSchoolId } = useAuth();
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState("");
  const [term, setTerm] = useState("1");
  const [data, setData] = useState(null);
  const [panel, setPanel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmHomolog, setConfirmHomolog] = useState(false);

  useEffect(() => {
    api.get("/academic-years", { params: scopeParams }).then((r) => {
      setYears(r.data);
      if (r.data[0]) setYearId((y) => y || r.data[0].id);
    });
  }, [scopeParams, selectedSchoolId]);

  const load = useCallback(() => {
    if (!yearId) return;
    const params = { academic_year_id: yearId, term_order: Number(term), ...scopeParams };
    api.get("/excellence", { params }).then((r) => setData(r.data)).catch(() => setData({ results: [], total: 0, classified: 0 }));
    api.get("/excellence/panel", { params }).then((r) => setPanel(r.data)).catch(() => setPanel(null));
  }, [yearId, term, scopeParams]);
  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/excellence/compute", { academic_year_id: yearId, term_order: Number(term) });
      toast.success(`Cálculo concluído: ${res.classified} classificados`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const homologate = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/excellence/homologate", { academic_year_id: yearId, term_order: Number(term) });
      toast.success(`Homologado: ${res.classified} conquistas registradas`);
      setConfirmHomolog(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const homologated = panel?.homologated;

  return (
    <div>
      <PageHeader title="Excelência Acadêmica" subtitle="Reconhece desempenho, frequência e disciplina no trimestre">
        <Button variant="outline" onClick={compute} disabled={busy || !yearId} data-testid="excellence-compute-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Calculator className="mr-1.5 h-4 w-4" /> Calcular</>}
        </Button>
        <Button onClick={() => setConfirmHomolog(true)} disabled={busy || !data?.total || homologated} data-testid="excellence-homologate-button" className="bg-blue-700 hover:bg-blue-800">
          <BadgeCheck className="mr-1.5 h-4 w-4" /> {homologated ? "Homologado" : "Homologar"}
        </Button>
      </PageHeader>

      <div className="mb-5 flex flex-wrap gap-3">
        <Select value={yearId} onValueChange={setYearId}>
          <SelectTrigger className="w-40" data-testid="excellence-year-select"><SelectValue placeholder="Ano letivo" /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.year}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={term} onValueChange={setTerm}>
          <SelectTrigger className="w-40" data-testid="excellence-term-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[1, 2, 3].map((t) => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {panel && panel.by_class.length > 0 && (
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="border-slate-200 bg-gradient-to-br from-blue-700 to-blue-500 p-5 text-white">
            <Trophy className="h-7 w-7" />
            <div className="mt-2 font-display text-4xl font-black">{panel.classified}</div>
            <div className="text-sm text-blue-100">Classificados · {panel.total} avaliados</div>
            {homologated && <Badge className="mt-2 bg-white/20">Homologado</Badge>}
          </Card>
          {panel.by_class.map((c) => (
            <Card key={c.class_id} data-testid="excellence-class-card" className="border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <span className="font-display text-lg font-bold text-slate-900">Turma {c.class_name}</span>
                <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-sm font-bold text-orange-700">{c.pct}%</span>
              </div>
              <div className="mt-1 text-sm text-slate-500">{c.classified} de {c.total} classificados</div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-orange-500" style={{ width: `${c.pct}%` }} /></div>
            </Card>
          ))}
        </div>
      )}

      {!data || data.results.length === 0 ? (
        <EmptyState icon={Award} title="Sem resultados" description="Selecione ano/trimestre e clique em Calcular." />
      ) : (
        <Card className="overflow-x-auto border-slate-200" data-testid="excellence-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Estudante</TableHead><TableHead className="text-center">Acadêmico</TableHead>
              <TableHead className="text-center">Frequência</TableHead><TableHead className="text-center">Disciplinar</TableHead>
              <TableHead className="text-center">Resultado</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.results.map((r) => (
                <TableRow key={r.id} data-testid={`excellence-row-${r.student_id}`} className={r.classified ? "bg-orange-50/40" : ""}>
                  <TableCell className="font-medium text-slate-800">{r.student_name}</TableCell>
                  <TableCell className="text-center"><Crit ok={r.academic} /></TableCell>
                  <TableCell className="text-center"><Crit ok={r.frequency} note={r.frequency_pct != null ? `${r.frequency_pct}%` : null} /></TableCell>
                  <TableCell className="text-center"><Crit ok={r.disciplinary} /></TableCell>
                  <TableCell className="text-center">
                    {r.classified
                      ? <Badge className="bg-orange-500 text-white"><Trophy className="mr-1 h-3 w-3" /> Excelência</Badge>
                      : <span className="text-xs text-slate-400">Não classificado</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-4 w-4" /> Motivos de não classificação são visíveis apenas a usuários autorizados (Coordenação/Direção).</p>

      <Dialog open={confirmHomolog} onOpenChange={setConfirmHomolog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Homologar Excelência</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">A homologação cria um registro histórico (snapshot) e gera as conquistas dos classificados. Alterações posteriores não modificam premiações já homologadas.</p>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmHomolog(false)}>Cancelar</Button><Button onClick={homologate} disabled={busy} data-testid="excellence-homologate-confirm" className="bg-blue-700 hover:bg-blue-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar homologação"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Crit({ ok, note }) {
  return (
    <span className="inline-flex items-center gap-1">
      {ok ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-500" />}
      {note && <span className="text-xs text-slate-400">{note}</span>}
    </span>
  );
}
